
import React, { useState, useEffect } from 'react';
import { HappyHourSchedule } from '@/types/menu';
import { Clock } from 'lucide-react';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';

interface HappyHourTimerProps {
  schedule: HappyHourSchedule;
  setIsHappyHour: React.Dispatch<React.SetStateAction<boolean>>;
  primaryColor: string;
  fontFamily?: string;
  isHappyHour?: boolean;
  themeColor?: string;
  currentLanguage?: LanguageCode;
}

const HappyHourTimer: React.FC<HappyHourTimerProps> = ({ 
  schedule,
  setIsHappyHour,
  primaryColor,
  fontFamily,
  isHappyHour = false,
  themeColor,
  currentLanguage = 'fr'
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [timeUntilStart, setTimeUntilStart] = useState<string>('');
  const [nextHappyHourInfo, setNextHappyHourInfo] = useState<string>('');
  
  const t = MENU_UI_TRANSLATIONS[currentLanguage];
  const dayNames = [t.sunday, t.monday, t.tuesday, t.wednesday, t.thursday, t.friday, t.saturday];
  
  useEffect(() => {
    if (!schedule.enabled) return;
    
    const updateTimes = () => {
      const now = new Date();
      const currentDay = now.getDay();
      
      const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
      const [endHour, endMinute] = schedule.endTime.split(':').map(Number);
      
      const startTime = new Date(now);
      startTime.setHours(startHour, startMinute, 0, 0);
      
      const endTime = new Date(now);
      endTime.setHours(endHour, endMinute, 0, 0);
      
      const isTodayActive = schedule.days.includes(currentDay);
      
      // Calculate time until start (if today is active and not started yet)
      if (isTodayActive && now < startTime) {
        const untilStartMs = startTime.getTime() - now.getTime();
        const untilStartHours = Math.floor(untilStartMs / (1000 * 60 * 60));
        const untilStartMinutes = Math.floor((untilStartMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (untilStartHours > 0) {
          setTimeUntilStart(`${untilStartHours}h ${untilStartMinutes}m`);
        } else {
          setTimeUntilStart(`${untilStartMinutes}m`);
        }
        setTimeRemaining('');
        setNextHappyHourInfo('');
      }
      // Calculate time remaining (if currently active)
      else if (isTodayActive && now >= startTime && now <= endTime) {
        const remainingMs = endTime.getTime() - now.getTime();
        const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
        const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (remainingHours > 0) {
          setTimeRemaining(`${remainingHours}h ${remainingMinutes}m`);
        } else {
          setTimeRemaining(`${remainingMinutes}m`);
        }
        setTimeUntilStart('');
        setNextHappyHourInfo('');
      }
      // Happy hour is over for today or not active today - find next one
      else {
        setTimeUntilStart('');
        setTimeRemaining('');
        
        // Find the next active day
        let daysUntilNext = 1;
        for (let i = 1; i <= 7; i++) {
          const checkDay = (currentDay + i) % 7;
          if (schedule.days.includes(checkDay)) {
            daysUntilNext = i;
            break;
          }
        }
        
        if (daysUntilNext === 1) {
          setNextHappyHourInfo(`${t.tomorrow} ${schedule.startTime} - ${schedule.endTime}`);
        } else {
          const nextDay = (currentDay + daysUntilNext) % 7;
          setNextHappyHourInfo(`${dayNames[nextDay]} ${schedule.startTime} - ${schedule.endTime}`);
        }
      }
    };
    
    updateTimes();
    const interval = setInterval(updateTimes, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [schedule, currentLanguage, t, dayNames]);

  // Check if current time is within happy hour
  useEffect(() => {
    const checkHappyHour = () => {
      if (!schedule.enabled) {
        setIsHappyHour(false);
        return;
      }
      
      const now = new Date();
      const currentDay = now.getDay(); // 0 is Sunday, 1 is Monday, etc.
      
      // Check if today is in the active days list
      if (!schedule.days.includes(currentDay)) {
        setIsHappyHour(false);
        return;
      }
      
      // Parse start and end times
      const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
      const [endHour, endMinute] = schedule.endTime.split(':').map(Number);
      
      const startTime = new Date(now);
      startTime.setHours(startHour, startMinute, 0, 0);
      
      const endTime = new Date(now);
      endTime.setHours(endHour, endMinute, 0, 0);
      
      // Check if current time is within happy hour
      const isActive = now >= startTime && now <= endTime;
      setIsHappyHour(isActive);
    };
    
    checkHappyHour();
    const interval = setInterval(checkHappyHour, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [schedule, setIsHappyHour]);
  
  if (!schedule.enabled) return null;

  const actualThemeColor = themeColor || primaryColor;
  
  // Determine message to display
  const getMessage = () => {
    if (timeRemaining) {
      return `${t.happyHourActive} - ${t.happyHourEndsIn} ${timeRemaining}`;
    }
    if (timeUntilStart) {
      return `${t.happyHourStartsIn} ${timeUntilStart}`;
    }
    if (nextHappyHourInfo) {
      return `${t.nextHappyHour} : ${nextHappyHourInfo}`;
    }
    return null;
  };
  
  const message = getMessage();
  if (!message) return null;
  
  // Different styling for active vs upcoming
  const isActive = !!timeRemaining;
  
  return (
    <div 
      className="w-full py-3 px-4 rounded-md flex items-center justify-center space-x-2 mb-4"
      style={{ 
        backgroundColor: isActive ? actualThemeColor : `${actualThemeColor}20`,
        color: isActive ? 'white' : actualThemeColor,
        fontFamily: fontFamily || 'Inter, sans-serif',
        border: isActive ? 'none' : `1px solid ${actualThemeColor}40`
      }}
    >
      <Clock className="h-4 w-4" />
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

export default HappyHourTimer;
